import { useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DeleteIcon from 'assets/icons/TableDelete.png';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { confirm } from 'components/MenuNew/ConfirmModal';
import DoneCheckMark from 'assets/images/doneCheckMark.png';
import { DownloadCopyButtonTitle } from 'redesign/BusinessTab/styles';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { selectCurrentBusiness, selectPrinterList } from 'redux/selectors';
import { MAX_ITEM_COUNT_IN_PRINTER, MODE } from 'pages/TableOrdering/config';
import { Box, Typography, IconButton, Tooltip, Popover } from '@mui/material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import {
  addPrinter,
  deletePrinter,
  getPrintersList,
  getPrinterTags,
  updatePrinter,
} from 'redux/actions/printers';
import { getPrinterTypeLabel } from 'constants/printers';
import {
  PrinterStatus,
  PrinterStatusBlock,
  PrinterStatusTitle,
  PrinterTicketStyle,
  TagsItem,
  TagsItemWrapper,
} from 'redesign/Printers/styles';

const defaultValues = {
  default: false,
  ipAddress: '',
  name: '',
  port: '',
  type: '',
  ticketStyle: 'regular',
  innerMacAddress: '',
  printerTags: [],
  connectionType: '',
};

const schemaPrinterItem = yup.object().shape({
  name: yup.string().required('field_is_required_'),
  ipAddress: yup.string().when(['type', 'innerMacAddress', 'connectionType'], {
    is: (type, innerMacAddress, connectionType) =>
      type != 'internal' && !innerMacAddress && connectionType !== 'ble',
    then: yup
      .string()
      .matches(
        '^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?).){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$',
        'this_field_is_invalid_'
      )
      .required('field_is_required_'),
  }),
  innerMacAddress: yup.string(),
  ticketStyle: yup.string().required('field_is_required_'),
  port: yup
    .string()
    .nullable()
    .when(['type', 'innerMacAddress', 'connectionType'], {
      is: (type, innerMacAddress, connectionType) =>
        type != 'internal' && !innerMacAddress && connectionType !== 'ble',
      then: yup.string().required('field_is_required_'),
    }),

  type: yup.string().required('field_is_required_'),
  printerTags: yup.array(),
  _id: yup.string(),
});

const usePrinters = () => {
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const [mode, setMode] = useState(MODE.CREATE);
  const [loading, setLoading] = useState(false);
  const [openPrinter, setOpenPrinters] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(MAX_ITEM_COUNT_IN_PRINTER);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentTagList, setCurrentTagList] = useState([]);
  const [shouldDirtyPrinterTags, setShouldDirtyPrinterTags] = useState(false);

  const currentBusiness = useSelector(selectCurrentBusiness);
  const printerList = useSelector(selectPrinterList);

  const formMethods = useForm({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(schemaPrinterItem),
  });

  useEffect(() => {
    if (currentBusiness?._id) {
      dispatch(getPrinterTags());
      dispatch(getPrintersList());
    }
  }, []);

  const { watch, setValue, resetField, formState } = formMethods;

  watch((data, { name }) => {
    if (name === 'type') {
      resetField('ipAddress', '');
      resetField('port', '');
      setValue('port', '');
      setValue('ipAddress', '');
    }
  });

  watch((data, { name }) => {
    if (name === 'printerTags') {
      setShouldDirtyPrinterTags(true);
    }
  });

  const handlePopoverOpen = (event, tags) => {
    setAnchorEl(event.currentTarget);
    setCurrentTagList(tags);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setCurrentTagList([]);
  };

  const open = Boolean(anchorEl);

  const printerListFiltered = printerList?.map((printer) => ({
    id: printer?._id,
    name: printer?.name || '',
    ipAddress: printer?.ipAddress || '',
    port: printer?.port || null,
    type: printer?.type || '',
    status: printer?.status,
    ticketStyle: printer?.ticketStyle || defaultValues.ticketStyle,
    printerTags: printer?.printerTags || [],
    isDefault: printer?.default,
    innerMacAddress: printer?.innerMacAddress ?? '',
    connectionType: printer?.connectionType ?? '',
  }));

  const iconStyle = useCallback((isOnline) => {
    const baseStyle = { fontSize: '10px' };
    if (isOnline) {
      return {
        ...baseStyle,
        color: '#11AF22',
      };
    }
    return {
      ...baseStyle,
      fill: 'none',
      stroke: 'rgba(0, 0, 0, 0.24)',
      strokeWidth: 1.5,
    };
  }, []);

  const handleEdit = (data) => {
    const newData = {
      default: data?.isDefault ?? false,
      ipAddress: data?.ipAddress ?? '',
      name: data?.name ?? '',
      port: data?.port ?? '',
      type: data?.type ?? '',
      ticketStyle: data?.ticketStyle,
      innerMacAddress: data?.innerMacAddress,
      printerTags: data?.printerTags ?? [],
      id: data?.id,
      connectionType: data?.connectionType ?? '',
    };
    formMethods.reset(newData);
    setMode(MODE.EDIT);
    setOpenPrinters(true);
  };

  const handleDelete = async (e, reportItem) => {
    e.stopPropagation();
    const { id, haveEvents } = reportItem;
    const option = {
      title: t('Confirm Printer Deletion'),
      message: t('settings.are_delete_printer'),
      cancelText: t('orders.cancel'),
      confirmText: t('table_ordering.yes_delete'),
      confirmColor: 'primary',
      background: 'rgba(226, 21, 8, 1)',
    };

    if (haveEvents) {
      option.message =
        'The printer you want to remove has assigned events. Do you want to remove it';
      option.cancelText = 'No';
      option.confirmText = 'Yes';
    }
    const confirmed = await confirm(option);
    if (confirmed) {
      dispatch(deletePrinter(id));
      return true;
    }
  };

  const printerColumns = [
    {
      field: 'name',
      headerName: t('Name'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: ({ row }) => {
        if (row?.name) {
          return (
            <Tooltip title={row?.name} placement={'top'}>
              <DownloadCopyButtonTitle>{row?.name}</DownloadCopyButtonTitle>
            </Tooltip>
          );
        }
      },
    },
    {
      field: 'ipAddress',
      headerName: t('IP Address'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: ({ row }) => {
        return <Typography>{row?.ipAddress}</Typography>;
      },
    },
    {
      field: 'port',
      headerName: t('Port'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
    },
    {
      field: 'type',
      headerName: t('Type'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: ({ row }) => {
        return <Typography>{getPrinterTypeLabel(row?.type)}</Typography>;
      },
    },
    {
      field: 'status',
      headerName: t('Status'),
      flex: 1,
      sortable: false,
      filterable: false,
      minWidth: 100,
      editable: false,
      renderCell: ({ row }) => {
        return (
          <PrinterStatusBlock>
            <PrinterStatus>
              <FiberManualRecordIcon sx={iconStyle(row?.status)} />
              <PrinterStatusTitle>
                {row?.status ? `Online` : 'Offline'}
              </PrinterStatusTitle>
            </PrinterStatus>
          </PrinterStatusBlock>
        );
      },
    },
    {
      field: 'ticketStyle',
      headerName: t('Ticket font size'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: ({ row }) => {
        return <PrinterTicketStyle>{row?.ticketStyle}</PrinterTicketStyle>;
      },
    },
    {
      field: 'printerTags',
      headerName: t('Tags'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: ({ row }) => {
        if (row?.printerTags && row?.printerTags?.length > 0) {
          return (
            <>
              <TagsItem
                label={`${row?.printerTags?.length} tags`}
                aria-owns={open ? 'mouse-over-popover' : undefined}
                aria-haspopup="true"
                onMouseEnter={(event) =>
                  handlePopoverOpen(event, row?.printerTags)
                }
                onMouseLeave={handlePopoverClose}
              />
              <Popover
                id="mouse-over-popover"
                sx={{ pointerEvents: 'none' }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus
                PaperProps={{
                  sx: { width: '210px' },
                }}
              >
                {currentTagList?.map((item, index) => {
                  return (
                    <TagsItemWrapper key={`${index}-item`}>
                      <TagsItem label={`${item}`} />
                    </TagsItemWrapper>
                  );
                })}
              </Popover>
            </>
          );
        }
      },
    },
    {
      field: 'isDefault',
      headerName: t('Default'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: ({ row }) => {
        if (row?.isDefault) {
          return <img src={DoneCheckMark} alt="done" />;
        } else {
          return <></>;
        }
      },
    },
    {
      id: 'tools',
      field: 'Tools',
      type: 'actions',
      width: 100,
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex' }}>
            <IconButton
              aria-label="delete"
              onClick={() => handleEdit(params?.row)}
            >
              <ModeEditOutlineOutlinedIcon />
            </IconButton>
            <IconButton
              aria-label="delete"
              onClick={(e) => {
                handleDelete(e, params?.row);
              }}
            >
              <img src={DeleteIcon} alt="delete icon" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleCancel = () => {
    setOpenPrinters(false);
    setTimeout(() => {
      formMethods.reset(defaultValues);
      formMethods.clearErrors();
      setShouldDirtyPrinterTags(false);
      setMode(MODE.CREATE);
    }, 300);
  };

  const createPrinter = (values) => {
    setLoading(true);
    try {
      dispatch(addPrinter({ ...values })).then(() => {
        setOpenPrinters(false);
        setTimeout(() => {
          formMethods.reset(defaultValues);
          formMethods.clearErrors();
          setShouldDirtyPrinterTags(false);
          setMode(MODE.CREATE);
        }, 300);
      });
    } catch (error) {
      console.log(error, 'Error');
    } finally {
      setLoading(false);
    }
  };

  const editPrinter = (values) => {
    setLoading(true);
    try {
      // Note: The findCurrentPrinter function doesn't require continuous checking.
      const findCurrentPrinter = printerList.find(
        (item) => item?._id === values.id
      );
      const requestData = {
        ...findCurrentPrinter,
        ...values,
      };
      dispatch(updatePrinter({ ...requestData })).then(() => {
        setOpenPrinters(false);
        setTimeout(() => {
          formMethods.reset(defaultValues);
          formMethods.clearErrors();
          setShouldDirtyPrinterTags(false);
          setMode(MODE.CREATE);
        }, 300);
      });
    } catch (error) {
      console.log(error, 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (values) => {
    if (mode === MODE.CREATE) {
      createPrinter(values);
    } else if (mode === MODE.EDIT) {
      editPrinter(values);
    }
  };

  return {
    openPrinter,
    setOpenPrinters,
    mode,
    handleCancel,
    loading,
    formMethods,
    handleSubmit,
    printerListFiltered,
    printerColumns,
    setRowsPerPage,
    rowsPerPage,
    shouldDirtyPrinterTags,
    formState,
  };
};

export default usePrinters;
